import { useQueryClient } from '@tanstack/react-query';
import apis from '@api/api';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { useEffect, useRef } from 'react';
import { DefaultRootState } from '@reduxLocal/persistState';
import { getConversations } from '@api/whatsappApi';

// Retry utility function for getAllMessagingUsers with exponential backoff
// First retry: 1 minute, Second retry: 3 minutes, Subsequent retries: every 5 minutes
const getAllMessagingUsersWithRetry = async (page: number, pageSize: number, filterExternal?: boolean) => {
	const retryDelays = [60000, 180000, 300000]; // 1 min, 3 min, 5 min
	let attempt = 0;
	
	while (true) {
		try {
			const response = await apis.getAllMessagingUsers(page, pageSize, filterExternal);
			return response;
		} catch (error) {
			attempt++;
			const delayIndex = Math.min(attempt - 1, retryDelays.length - 1);
			const delay = retryDelays[delayIndex];
			
			console.warn(`❌ getAllMessagingUsers failed (attempt ${attempt}), retrying in ${delay / 1000}s:`, error);
			
			if (attempt > retryDelays.length) {
				// After all retries, continue with 5 minute intervals
				await new Promise(resolve => setTimeout(resolve, retryDelays[retryDelays.length - 1]));
			} else {
				await new Promise(resolve => setTimeout(resolve, delay));
			}
		}
	}
};

export const useAllUsers = ({ contextDispatch, setLoading }) => {
	const queryClient = useQueryClient();
	const lastDataRef = useRef(null);
	const activeFilter = useSelector(
		(state: DefaultRootState) => state.UI.messageAppFilter
	);
	const externalFilter = useSelector(
		(state: DefaultRootState) => state.UI.messageExternalFilter
	);
	const activeWhatsappNumber = useSelector(
		(state: DefaultRootState) =>
			state.UI.messageOptions?.activeWhatsappNumber
	);
	const whatsappNumbers = useSelector(
		(state: DefaultRootState) =>
			state.UI.messageOptions?.whatsappNumbers || []
	);

	// Use useQuery for reactive data fetching
	const {
		data: users,
		isLoading,
		refetch,
	} = useQuery({
		queryKey: [
			'all-users',
			{ filter: activeFilter, externalFilter, page: 1, limit: 50 },
		],
		queryFn: async () => {
			console.log('🔍 useAllUsers queryFn called:', {
				activeFilter,
				externalFilter,
			});
			let users = [];
			if (activeFilter === 'whatsapp' || activeFilter === 'all') {
				// For 'all' filter, fetch from all WhatsApp numbers
				// For 'whatsapp' filter, fetch from selected number only
				if (activeFilter === 'all') {
					// Fetch conversations from all WhatsApp numbers
					// Backend now automatically collects all identifiers, so we only need to query once
					// Use the first identifier (backend will query with all user identifiers)
					if (whatsappNumbers && whatsappNumbers.length > 0) {
						try {
							// Query with first identifier - backend will automatically use all user identifiers
							const firstIdentifier = whatsappNumbers[0];
							console.log(`📱 Querying conversations with first identifier (backend will use all identifiers): ${firstIdentifier}`);
							
							const response = await getConversations(firstIdentifier, {
								page: 1,
								limit: 50,
							});
							
							// Handle response structure: could be array directly or object with conversations property
							let conversations = [];
							if (Array.isArray(response)) {
								conversations = response;
							} else if (response?.conversations && Array.isArray(response.conversations)) {
								conversations = response.conversations;
							} else if (response?.data?.conversations && Array.isArray(response.data.conversations)) {
								conversations = response.data.conversations;
							}
							
							// Ensure conversationId is set for each conversation
							users = conversations.map(conv => ({
								...conv,
								conversationId: conv.conversationId || conv._id || conv.id,
							}));
							
							console.log(`📱 Fetched ${users.length} conversations (backend queried with all user identifiers)`);
						} catch (error) {
							console.error('Error fetching all WhatsApp conversations:', error);
							users = [];
						}
					} else {
						console.warn('No WhatsApp numbers available for "all" filter');
						users = [];
					}
				} else {
					// Original 'whatsapp' filter logic - single number
					if (
						activeWhatsappNumber &&
						activeWhatsappNumber !== '123456123'
					) {
						try {
							const response = await getConversations(activeWhatsappNumber, {
								page: 1,
								limit: 50,
							});
							// Handle response structure: could be array directly or object with conversations property
							if (Array.isArray(response)) {
								users = response;
							} else if (response?.conversations && Array.isArray(response.conversations)) {
								users = response.conversations;
							} else if (response?.data?.conversations && Array.isArray(response.data.conversations)) {
								users = response.data.conversations;
							} else {
								console.warn('Unexpected response structure from getConversations:', response);
								users = [];
							}
							// Ensure conversationId is set for each conversation
							users = users.map(conv => ({
								...conv,
								conversationId: conv.conversationId || conv._id || conv.id,
							}));
						} catch (error) {
							console.error(
								'Error fetching WhatsApp conversations:',
								error
							);
							users = [];
						}
					} else {
						users = [];
					}
				}
			} else {
				console.log('📞 Calling getAllMessagingUsers API:', {
					page: 1,
					limit: 50,
					externalFilter,
					activeFilter,
				});
				const response = await getAllMessagingUsersWithRetry(
					1,
					50,
					externalFilter
				);
			
				users = response?.data || [];
				console.log('✅ Users array after extraction:', {
					userCount: users.length,
					isArray: Array.isArray(users),
					firstUser: users[0]
						? {
								_id: users[0]._id,
								user: users[0].user?._id,
								name:
									users[0].user?.profile_information?.name ||
									users[0].name,
								conversationId: users[0].conversationId, // Log conversationId
							}
						: null,
					// Check if conversationIds are present in first 3 users
					conversationIdsPresent: users.slice(0, 3).map((u) => ({
						_id: u._id,
						hasConversationId: !!u.conversationId,
						conversationId: u.conversationId,
					})),
				});
				// Store opposite count in the response for badge display
				if (response?.counts) {
					(users as any).oppositeCount = response.counts.opposite;
				}
			}

			console.log('🔄 useAllUsers queryFn returning:', {
				userCount: users.length,
				isArray: Array.isArray(users),
				activeFilter,
				externalFilter,
			});

			// Return users without mapping to prevent object recreation
			return users;
		},
		staleTime: 0, // Set to 0 to always fetch fresh data when queryKey changes
		gcTime: 1000 * 60 * 15, // Cache for 15 minutes
		refetchOnWindowFocus: false, // Prevent refetch when window regains focus
		refetchOnMount: true, // Always refetch on mount to ensure data is loaded on mobile
		refetchOnReconnect: true, // Refetch when network reconnects
		enabled: true,
	});

	// Update context when data changes (with optimized comparison to prevent unnecessary updates)
	useEffect(() => {
		// Always update context when users data is available (even if empty array)
		// This ensures the context is updated on mobile even if the query runs after mount
		if (users !== undefined && users !== lastDataRef.current) {
			// Only update context if users data reference has actually changed
			console.log('📊 useAllUsers: Data changed, updating context', {
				userCount: users?.length || 0,
				externalFilter,
				oppositeCount: (users as any)?.oppositeCount,
				firstUser: users?.[0]?._id || users?.[0]?.user?._id,
				isArray: Array.isArray(users),
			});
			lastDataRef.current = users;
			// Always dispatch, even if users is an empty array (to clear stale data)
			contextDispatch({ type: 'SET_DATA', payload: users || [] });
		} else if (users === undefined && lastDataRef.current === null) {
			// If users is undefined and we haven't set anything yet, ensure context starts with empty array
			console.log(
				'📊 useAllUsers: Initializing context with empty array'
			);
			lastDataRef.current = [];
			contextDispatch({ type: 'SET_DATA', payload: [] });
		}
	}, [users, contextDispatch]); // Added contextDispatch back - it's stable from useMessageList

	// Update loading state
	useEffect(() => {
		setLoading?.(isLoading);
	}, [isLoading]); // Removed setLoading to prevent infinite re-renders

	// Refetch when filter changes (activeFilter or externalFilter)
	useEffect(() => {
		// Invalidate queries with partial match to ensure all related queries are refetched
		// This ensures queries are refetched when switching between SmartSaaS and WhatsApp
		queryClient.invalidateQueries({
			queryKey: ['all-users'],
			refetchType: 'active', // Only refetch active queries
		});
		
		// Also explicitly refetch to ensure data is loaded immediately
		refetch();
		
		console.log('🔄 Filter changed, invalidating and refetching all-users queries:', {
			activeFilter,
			externalFilter
		});
	}, [externalFilter, queryClient, activeFilter, refetch]);

	const getAllUsers = async (page: number = 1, limit: number = 50) => {
		try {
			setLoading?.(true);
			// Use the same queryKey format as useQuery to ensure consistency
			const queryKey = [
				'all-users',
				{ filter: activeFilter, externalFilter, page, limit },
			];

			const userList = await queryClient.fetchQuery({
				queryKey,
				queryFn: async () => {
					let users = [];
					if (activeFilter === 'whatsapp' || activeFilter === 'all') {
						// For 'all' filter, fetch from all WhatsApp numbers
						// For 'whatsapp' filter, fetch from selected number only
						if (activeFilter === 'all') {
							// Fetch conversations from all WhatsApp numbers
							// Backend now automatically collects all identifiers, so we only need to query once
							// Use the first identifier (backend will query with all user identifiers)
							if (whatsappNumbers && whatsappNumbers.length > 0) {
								try {
									// Query with first identifier - backend will automatically use all user identifiers
									const firstIdentifier = whatsappNumbers[0];
									
									const response = await getConversations(firstIdentifier, {
										page,
										limit,
									});
									
									// Handle response structure: could be array directly or object with conversations property
									let conversations = [];
									if (Array.isArray(response)) {
										conversations = response;
									} else if (response?.conversations && Array.isArray(response.conversations)) {
										conversations = response.conversations;
									} else if (response?.data?.conversations && Array.isArray(response.data.conversations)) {
										conversations = response.data.conversations;
									}
									
									// Ensure conversationId is set for each conversation
									users = conversations.map(conv => ({
										...conv,
										conversationId: conv.conversationId || conv._id || conv.id,
									}));
								} catch (error) {
									console.error('Error fetching all WhatsApp conversations:', error);
									users = [];
								}
							} else {
								console.warn('No WhatsApp numbers available for "all" filter');
								users = [];
							}
						} else {
							// Original 'whatsapp' filter logic - single number
							if (
								activeWhatsappNumber &&
								activeWhatsappNumber !== '123456123'
							) {
							try {
								const response = await getConversations(
									activeWhatsappNumber,
									{
										page,
										limit,
									}
								);
								// Handle response structure: could be array directly or object with conversations property
								if (Array.isArray(response)) {
									users = response;
								} else if (response?.conversations && Array.isArray(response.conversations)) {
									users = response.conversations;
								} else if (response?.data?.conversations && Array.isArray(response.data.conversations)) {
									users = response.data.conversations;
								} else {
									console.warn('Unexpected response structure from getConversations:', response);
									users = [];
								}
								// Ensure conversationId is set for each conversation
								users = users.map(conv => ({
									...conv,
									conversationId: conv.conversationId || conv._id || conv.id,
								}));
							} catch (error) {
								console.error(
									'Error fetching WhatsApp conversations:',
									error
								);
								users = [];
							}
							} else {
								console.warn('No valid WhatsApp number configured');
								users = [];
							}
						}
					} else {
						const response = await getAllMessagingUsersWithRetry(
							page,
							limit,
							externalFilter
						);
						users = response?.data || [];
						// Store opposite count in the response for badge display
						if (response?.counts) {
							(users as any).oppositeCount =
								response.counts.opposite;
						}
					}

					// Safeguard: Ensure users is a valid array before mapping
					if (!Array.isArray(users)) {
						console.warn(
							'Users is not an array, defaulting to empty array:',
							users
						);
						users = [];
					}

					return users
						.map((item) => {
							// Safeguard: Skip null or undefined items
							if (!item) {
								console.warn(
									'Encountered null or undefined item in users array'
								);
								return null;
							}

							if (activeFilter === 'whatsapp' || activeFilter === 'all') {
								// Map WhatsApp conversation data to expected format
								return {
									_id: item._id || item.id,
									id: item._id || item.id,
									user: {
										_id:
											item.participants?.[0] ||
											item.participantPhone,
										name:
											item.contactInfo?.name ||
											item.contactName ||
											item.participantPhone,
										avatar:
											item.contactInfo?.profilePicture ||
											item.contactProfilePicture,
										phone:
											item.participants?.[0] ||
											item.participantPhone,
									},
									badge: item.unreadCount || 0,
									lastMessage: item.lastMessage,
									lastMessageTime:
										item.lastMessageAt ||
										item.lastMessageTime,
									status: item.status,
									messageCount: item.messageCount || 0,
									businessPhoneNumberId:
										item.businessPhoneNumberId,
									assignedTo: item.assignedTo,
									tags: item.tags || [],
								};
							} else {
								// Regular user data
								return {
									...item,
									badge: item.badge || 0,
								};
							}
						})
						.filter(Boolean);
				},
				staleTime: 1000 * 60 * 5,
			});

			// Always update context with fetched data (for both useQuery and getAllUsers)
			if (page === 1) {
				contextDispatch({ type: 'SET_DATA', payload: userList });
			}

			return userList;
		} catch (error) {
			console.error('Error fetching users:', error);
			return [];
		} finally {
			setLoading?.(false);
		}
	};

	return { getAllUsers, refetch, isLoading };
};
